// Existing SidePanel adapted to the unified EM2c simulation systems.
// Exposes gameplay controls/status plus habitat occupancy and closed-loop outputs.

import React from "react";
import { useGame } from "../game/em2c-store";
import { EM2C_BUILDINGS } from "../game/data/em2c-buildings";
import {
  getBuildingPriority,
  getPriorityLabel,
  getPriorityColor,
} from "../game/systems/ColonyResourceController";
import { Trash2, Power, X, AlertTriangle, Factory, Gauge, Users, Recycle } from "lucide-react";

const RESOURCE_LABELS = {
  power: "Power", water: "Clean Water", oxygen: "Oxygen", food: "Food",
  refined_metal: "Refined Metal", machine_parts: "Machine Parts", polymers: "Polymers",
  credits: "Credits", raw_water: "Raw Water", potable_water: "Clean Water (Legacy)",
  metal_ore: "Metal Ore", raw_ore: "Raw Ore", sorted_ore: "Sorted Ore",
  structural_components: "Structural Components", electronic_components: "Electronics",
  utility_components: "Utility Components", specialized_alloys: "Specialized Alloys",
  processed_minerals: "Processed Minerals", components: "Components",
  building_modules: "Building Modules", habitat_module: "Habitat Module",
  storage_module: "Storage Module", reactor_module: "Reactor Module",
  corridor_module: "Corridor Module", tower_module: "Tower Module",
  greenhouse_module: "Greenhouse Module", extraction_module: "Extraction Module",
  lab_module: "Laboratory Module", waste: "Recyclable Waste", organic_waste: "Organic Waste",
  wastewater: "Wastewater", fertilizer: "Fertilizer", nutrients: "Nutrients",
  raw_food: "Raw Food", processed_food: "Processed Food", reactor_fuel: "Reactor Fuel",
  rocket_propellant: "Rocket Propellant", regolith: "Regolith", ice: "Ice",
  glass_feedstock: "Glass Feedstock", industrial_gases: "Industrial Gases",
  co2: "CO₂", nitrogen: "Nitrogen", research_points: "Research Points",
};

// Hard UI fallbacks make habitat identity visible even if a legacy save was
// created before runtime resource rules added the same metadata.
const HABITAT_CAPACITY = {
  habitat_module: 6,
  crew_quarters: 4,
  family_habitat: 8,
  habitat_complex: 24,
};
const RESIDENT_INPUTS = { water: 0.3, oxygen: 0.4, food: 0.15 };
const RESIDENT_OUTPUTS = { co2: 0.32, wastewater: 0.24, organic_waste: 0.08, waste: 0.02 };

const STATE_LABELS = {
  operational: { text: "Operational", color: "#00fa9a" },
  disabled: { text: "Disabled", color: "#ffc107" },
  under_construction: { text: "Under Construction", color: "#ffc107" },
  constructing: { text: "Under Construction", color: "#ffc107" },
  standby: { text: "Standby", color: "#8fa0b3" },
  output_full: { text: "Standby — Output Full", color: "#ffc107" },
  no_workers: { text: "Stalled — No Workers", color: "#ff8c00" },
  power_shed: { text: "Automatic Power Shed", color: "#ff8c00" },
  no_power: { text: "Stalled — No Power", color: "#ff3366" },
  no_water: { text: "Stalled — No Water", color: "#ff3366" },
  no_oxygen: { text: "Stalled — No Oxygen", color: "#ff3366" },
  input_shortage: { text: "Stalled — Input Shortage", color: "#ff3366" },
  damaged: { text: "Damaged", color: "#ff3366" },
  reduced: { text: "Reduced Capacity", color: "#ffc107" },
  reduced_efficiency: { text: "Reduced Efficiency", color: "#ffc107" },
};

const STOPPED_STATES = new Set([
  "disabled", "standby", "output_full", "no_workers", "power_shed",
  "no_power", "no_water", "no_oxygen", "input_shortage", "damaged",
]);

function labelForResource(key) {
  return RESOURCE_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function Bar({ value, max, color, label }) {
  const safeMax = Math.max(0.0001, max || 1);
  const pct = Math.max(0, Math.min(100, (value / safeMax) * 100));
  return (
    <div className="mb-2">
      <div className="flex justify-between mb-1">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{label}</span>
        <span className="text-[10px] font-mono text-white">{Math.floor(value)}/{Math.floor(safeMax)}</span>
      </div>
      <div className="h-1 bg-white/5">
        <div className="h-full transition-[width] duration-300" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}` }} />
      </div>
    </div>
  );
}

function IOList({ title, values, color, prefix, suffix = "" }) {
  const entries = Object.entries(values || {});
  if (!entries.length) return null;
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mb-0.5">{title}</div>
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between text-[11px] font-mono gap-3">
          <span className="text-slate-400 truncate">{labelForResource(k)}</span>
          <span style={{ color }}>{prefix ?? (title === "Inputs" ? "-" : "+")}{v}{suffix}</span>
        </div>
      ))}
    </div>
  );
}

function assignedResidentsFor(buildingId, buildings, colonistCount) {
  let remaining = Math.max(0, colonistCount || 0);
  for (const building of buildings || []) {
    const capacity = Number(EM2C_BUILDINGS[building.type]?.residentCapacity || HABITAT_CAPACITY[building.type] || 0);
    if (!capacity || building.progress < 1 || building.active === false) continue;
    const assigned = Math.min(capacity, remaining);
    if (building.id === buildingId) return assigned;
    remaining -= assigned;
  }
  return 0;
}

export default function SidePanel() {
  const selectedId = useGame((s) => s.selectedId);
  const buildings = useGame((s) => s.buildings);
  const colonistCount = useGame((s) => s.colonists.length);
  const b = buildings.find((x) => x.id === selectedId);
  const toggleActive = useGame((s) => s.toggleActive);
  const demolish = useGame((s) => s.demolish);
  const selectedRecipes = useGame((s) => s.selectedRecipes || {});
  const setRecipe = useGame((s) => s.setRecipe);
  const getBuildingRecipes = useGame((s) => s.getBuildingRecipes);
  const buildingOverrides = useGame((s) => s.buildingOverrides || {});
  const setBuildingPriority = useGame((s) => s.setBuildingPriority);
  const resetBuildingPriority = useGame((s) => s.resetBuildingPriority);

  if (!b) return null;
  const def = EM2C_BUILDINGS[b.type];
  if (!def) return null;

  const accent = def.accent || def.color || "#00e5ff";
  const recipes = typeof getBuildingRecipes === "function" ? getBuildingRecipes(b.id) : [];
  const selectedRecipeId = selectedRecipes[b.id] || null;
  const selectedRecipe = recipes.find((r) => r.id === selectedRecipeId) || null;
  const inputs = recipes.length > 0 ? (selectedRecipe?.inputs || {}) : (def.productionInputs || {});
  const outputs = recipes.length > 0 ? (selectedRecipe?.outputs || {}) : (def.productionOutputs || {});
  const storage = def.storageCapacity || {};
  const secondaryOutputs = def.nonBlockingOutputs || {};
  const residentCapacity = Number(def.residentCapacity || HABITAT_CAPACITY[b.type] || 0);
  const residentInputs = residentCapacity > 0 ? (def.residentInputsPerColonist || RESIDENT_INPUTS) : {};
  const residentByproducts = residentCapacity > 0 ? (def.byproductsPerResident || RESIDENT_OUTPUTS) : {};
  const computedResidents = residentCapacity > 0 ? assignedResidentsFor(b.id, buildings, colonistCount) : 0;
  const residents = Math.min(residentCapacity, Math.max(0, Number.isFinite(Number(b.residents)) ? Number(b.residents) : computedResidents));
  const visibleResidents = residentCapacity > 0 && residents === 0 && computedResidents > 0 ? computedResidents : residents;
  const condition = b.condition != null ? b.condition : 1;
  const condColor = condition > 0.7 ? "#00fa9a" : condition > 0.4 ? "#ffc107" : "#ff3366";
  const condLabel = condition > 0.7 ? "Good" : condition > 0.4 ? "Worn" : "Critical";
  const isStopped = STOPPED_STATES.has(b.state);
  const stateInfo = STATE_LABELS[b.state] || { text: b.state || "Unknown", color: "#888" };
  const defaultPriority = getBuildingPriority(b.type);
  const currentPriority = buildingOverrides[b.id] ?? defaultPriority;
  const priorityOverridden = buildingOverrides[b.id] !== undefined;
  const recipePct = selectedRecipe ? Math.max(0, Math.min(100, ((b.recipeProgress || 0) / Math.max(selectedRecipe.time, 0.001)) * 100)) : 0;
  const activeResidentOutputs = Object.fromEntries(
    Object.entries(residentByproducts).map(([key, rate]) => [key, Number((rate * visibleResidents).toFixed(2))])
  );

  return (
    <div
      className="absolute top-16 right-3 z-20 pointer-events-auto w-72 bg-[#0A0C10]/90 backdrop-blur-md border border-white/10 max-h-[calc(100vh-5rem)] overflow-y-auto"
      style={{ fontFamily: "Rajdhani, sans-serif", scrollbarWidth: "thin" }}
      data-testid="side-panel"
    >
      <div className="p-3 border-b border-white/10 flex items-center gap-2">
        <div className="w-2 h-8" style={{ background: accent, boxShadow: `0 0 12px ${accent}` }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{def.category} · Tier {def.tier || 0}</div>
          <div className="text-base font-semibold uppercase tracking-wide text-white truncate">#{b.buildNumber || buildings.findIndex((x) => x.id === b.id) + 1} {def.name}</div>
        </div>
        <button onClick={() => useGame.getState().selectBuilding(null)} className="text-slate-500 hover:text-white"><X size={14} /></button>
      </div>

      <div className="p-3">
        {b.progress != null && b.progress < 1 ? (
          <div className="mb-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ffc107] mb-1">Under construction</div>
            <div className="h-1 bg-white/5"><div className="h-full bg-[#ffc107]" style={{ width: `${b.progress * 100}%`, boxShadow: "0 0 8px #ffc107" }} /></div>
            <div className="text-[10px] font-mono text-slate-400 mt-1">{Math.floor(b.progress * 100)}%</div>
          </div>
        ) : (
          <>
            <div className="mb-3 px-2 py-1.5 border text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5" style={{ color: stateInfo.color, borderColor: `${stateInfo.color}40`, background: `${stateInfo.color}10` }}>
              {isStopped && <AlertTriangle size={11} />}
              <span>{stateInfo.text}</span>
              {b.controllerReason && b.controllerReason !== stateInfo.text && <span className="ml-auto normal-case tracking-normal text-[9px] opacity-70 truncate max-w-24" title={b.controllerReason}>{b.controllerReason}</span>}
            </div>

            <Bar value={condition * 100} max={100} color={condColor} label={`Condition · ${condLabel}`} />
            <div className="text-xs font-mono text-slate-300 leading-snug mb-3 mt-2">{def.description}</div>

            {residentCapacity > 0 && (
              <div className="mb-3 border border-cyan-400/25 bg-cyan-500/[0.04] p-2" data-testid="habitat-resident-info">
                <div className="flex items-center gap-1.5 mb-2">
                  <Users size={12} className="text-cyan-400" />
                  <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">Holds Colonists</div>
                  <span className="ml-auto text-sm font-mono font-bold text-white">{visibleResidents}/{residentCapacity}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <IOList title="Per Resident Inputs" values={residentInputs} color="#ffc107" prefix="-" suffix="/s" />
                  <IOList title="Per Resident Outputs" values={residentByproducts} color="#00fa9a" prefix="+" suffix="/s" />
                </div>
                <div className="mt-2 pt-2 border-t border-white/10">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mb-1">Current Output · {visibleResidents} residents</div>
                  {Object.entries(activeResidentOutputs).map(([key, rate]) => (
                    <div key={key} className="flex justify-between text-[11px] font-mono">
                      <span className="text-slate-400">{labelForResource(key)}</span>
                      <span className="text-[#00fa9a]">+{rate}/s</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {recipes.length > 0 && (
              <div className="mb-3 border border-white/10 bg-white/[0.02] p-2" data-testid="recipe-control">
                <div className="flex items-center gap-1.5 mb-2"><Factory size={12} className="text-cyan-400" /><div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">Production Recipe</div></div>
                <select value={selectedRecipeId || ""} onChange={(e) => setRecipe?.(b.id, e.target.value || null)} className="w-full bg-[#11151b] border border-white/15 text-[11px] font-mono text-white px-2 py-1.5 outline-none focus:border-cyan-400" data-testid="recipe-select">
                  <option value="">Standby — select recipe</option>
                  {recipes.map((recipe) => <option key={recipe.id} value={recipe.id}>{recipe.name}</option>)}
                </select>
                {selectedRecipe && (
                  <div className="mt-2">
                    <div className="flex justify-between text-[9px] font-mono uppercase tracking-widest text-slate-500 mb-1"><span>Cycle Progress</span><span>{Math.floor(recipePct)}% · {selectedRecipe.time}s</span></div>
                    <div className="h-1 bg-white/5 mb-2"><div className="h-full bg-cyan-400 transition-[width] duration-200" style={{ width: `${recipePct}%`, boxShadow: "0 0 8px #22d3ee" }} /></div>
                    <div className="grid grid-cols-2 gap-2"><IOList title="Inputs" values={selectedRecipe.inputs} color="#ffc107" /><IOList title="Outputs" values={selectedRecipe.outputs} color="#00fa9a" /></div>
                    {selectedRecipe.workers > 0 && <div className="mt-1 text-[10px] font-mono text-slate-500">Recipe crew: {selectedRecipe.workers}</div>}
                  </div>
                )}
              </div>
            )}

            <div className="mb-3 border border-white/10 bg-white/[0.02] p-2" data-testid="priority-control">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Gauge size={12} style={{ color: getPriorityColor(currentPriority) }} />
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Power Priority</div>
                <span className="ml-auto text-[9px] font-mono" style={{ color: getPriorityColor(currentPriority) }}>{priorityOverridden ? "Manual" : "Auto"}</span>
              </div>
              <div className="text-[10px] font-mono mb-2" style={{ color: getPriorityColor(currentPriority) }}>{getPriorityLabel(currentPriority)}</div>
              <div className="grid grid-cols-6 gap-1">
                {[0, 1, 2, 3, 4, 5].map((p) => <button key={p} onClick={() => setBuildingPriority?.(b.id, p)} className={`h-6 border text-[9px] font-mono ${currentPriority === p && priorityOverridden ? "bg-white/10" : "bg-transparent"}`} style={{ borderColor: currentPriority === p ? getPriorityColor(p) : "rgba(255,255,255,0.12)", color: currentPriority === p ? getPriorityColor(p) : "#7b8794" }} title={getPriorityLabel(p)}>P{p}</button>)}
              </div>
              {priorityOverridden && <button onClick={() => resetBuildingPriority?.(b.id)} className="mt-1.5 text-[9px] font-mono uppercase tracking-widest text-slate-500 hover:text-cyan-400">Reset to automatic P{defaultPriority}</button>}
            </div>

            <div className="mt-3 mb-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Rates</div>
              <div className="space-y-1 text-xs font-mono">
                {def.powerConsumption > 0 && <div className="flex justify-between"><span className="text-slate-400">Power</span><span style={{ color: "#ffc107" }}>-{def.powerConsumption}</span></div>}
                {def.waterConsumption > 0 && <div className="flex justify-between"><span className="text-slate-400">Clean Water</span><span style={{ color: "#ffc107" }}>-{def.waterConsumption}</span></div>}
                {def.oxygenConsumption > 0 && <div className="flex justify-between"><span className="text-slate-400">Oxygen</span><span style={{ color: "#ffc107" }}>-{def.oxygenConsumption}</span></div>}
                {recipes.length === 0 && Object.entries(inputs).map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-400">{labelForResource(k)}</span><span style={{ color: "#ffc107" }}>-{v}</span></div>)}
                {recipes.length === 0 && Object.entries(outputs).map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-400">{labelForResource(k)}</span><span style={{ color: isStopped ? "#555" : "#00fa9a", textDecoration: isStopped ? "line-through" : "none" }}>+{v}</span></div>)}
                {def.workersRequired > 0 && <div className="flex justify-between"><span className="text-slate-400">Workers</span><span style={{ color: (b.workers || 0) < def.workersRequired ? "#ff8c00" : "#fff" }}>{b.workers || 0}/{def.workersRequired}</span></div>}
                {b.reducedCapacity && <div className="flex justify-between"><span className="text-slate-400">Controller capacity</span><span className="text-[#ffc107]">{Math.round(b.reducedCapacity * 100)}%</span></div>}
              </div>
            </div>

            {Object.keys(secondaryOutputs).length > 0 && (
              <div className="mb-3 border border-[#00fa9a]/15 bg-[#00fa9a]/[0.025] p-2" data-testid="secondary-outputs">
                <div className="flex items-center gap-1.5 mb-1.5"><Recycle size={11} className="text-[#00fa9a]" /><div className="text-[9px] font-mono uppercase tracking-widest text-slate-400">Secondary / Recyclable Outputs</div></div>
                {Object.entries(secondaryOutputs).map(([key, rate]) => <div key={key} className="flex justify-between text-[11px] font-mono"><span className="text-slate-400">{labelForResource(key)}</span><span className="text-[#00fa9a]">+{rate}/s</span></div>)}
                <div className="mt-1 text-[9px] font-mono text-slate-600">Full by-product storage never shuts down the primary building.</div>
              </div>
            )}

            {Object.keys(storage).length > 0 && (
              <div className="mb-2 border border-white/10 bg-white/[0.015] p-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Adds Storage Capacity</div>
                {Object.entries(storage).map(([k, v]) => <div key={k} className="flex justify-between text-xs font-mono"><span className="text-slate-400">{labelForResource(k)}</span><span className="text-white">+{v}</span></div>)}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex border-t border-white/10 sticky bottom-0 bg-[#0A0C10]/95">
        <button data-testid="btn-toggle-active" onClick={() => toggleActive(b.id)} className={`flex-1 py-2 flex items-center justify-center gap-1 text-[10px] font-mono uppercase tracking-widest border-r border-white/10 ${b.active !== false ? "text-[#00fa9a] hover:bg-[#00fa9a]/10" : "text-[#ffc107] hover:bg-[#ffc107]/10"}`}><Power size={12} /> {b.active !== false ? "Active" : "Idle"}</button>
        {b.type !== "command_center" && <button data-testid="btn-demolish" onClick={() => demolish(b.id)} className="flex-1 py-2 flex items-center justify-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#ff3366] hover:bg-[#ff3366]/10"><Trash2 size={12} /> Demolish</button>}
      </div>
    </div>
  );
}
